import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import useUnreadNotifications from "@/hooks/useUnreadNotifications";
import { logWarn } from "@/lib/logger";

const LABELS = {
  markAll: { it: "Segna tutte", en: "Mark all read", he: "סמן הכל", ru: "Прочитать все" },
  empty: { it: "Nessuna notifica", en: "No notifications", he: "אין התראות", ru: "Нет уведомлений" },
};
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Compact bell icon + popover panel with the 10 most recent notifications.
 * Clicking one marks it read and navigates to link_url if present.
 * The hook already fires toasts on new arrivals.
 */
export default function NotificationBell() {
  const { user, lang } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const { count, refresh, markAllRead } = useUnreadNotifications({ enabled: !!user });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/notifications");
      setItems((data || []).slice(0, 10));
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const openItem = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n.id}/read`); refresh(); }
    catch (e) { logWarn("NotificationBell: mark-read failed", e); }
    if (n.link_url) {
      if (n.link_url.startsWith("http")) {
        window.location.href = n.link_url;
      } else {
        nav(n.link_url);
      }
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative flex items-center justify-center w-10 h-10 rounded-full text-[#4A3F30] hover:bg-[#EFE7D5] hover:text-[#1A1610] transition-colors"
          title={t("notifications", lang)}
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute top-1 end-1 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-semibold leading-4 text-center"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-white border-[#E6DFCF] text-[#1A1610] w-[360px] max-h-[70vh] overflow-hidden p-0"
        data-testid="notif-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6DFCF]">
          <p className="text-xs tracking-[0.25em] uppercase text-[#B8965A]">
            {t("notifications", lang)}
          </p>
          {count > 0 && (
            <button
              data-testid="notif-mark-all-read"
              onClick={markAllRead}
              className="text-[11px] text-[#7A6E5A] hover:text-[#1A1610] flex items-center gap-1"
            >
              <CheckCheck className="h-3 w-3" />
              {LABELS.markAll[lang] || LABELS.markAll.en}
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {busy && <p className="text-xs text-[#9A8B72] p-4">…</p>}
          {!busy && items.length === 0 && (
            <p className="text-xs text-[#9A8B72] p-6 text-center">
              {LABELS.empty[lang] || LABELS.empty.en}
            </p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`notif-item-${n.id}`}
              className={`w-full text-start px-4 py-3 border-b border-[#F0EAD8] hover:bg-[#FBF8F1] transition-colors ${!n.read ? "bg-[#FDF9EE]" : ""}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#B8965A] flex-shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-[#1A1610] truncate">{n.subject}</p>
                  <p className="text-[11px] text-[#7A6E5A] mt-0.5 line-clamp-2">{n.body}</p>
                  <p className="text-[10px] text-[#9A8B72] mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
